import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';

import { api } from '../api/client';
import type { ComplaintSortColumn } from '../api/client';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { StatusBadge, SensitiveBadge, OverdueBadge } from '../components/Badges';
import { formatDateTime } from '../format';

const PAGE_SIZE = 50;

const COLUMNS: { key: ComplaintSortColumn; label: string }[] = [
  { key: 'reference', label: 'Reference' },
  { key: 'complainant', label: 'Complainant' },
  { key: 'application_loan', label: 'Application / loan' },
  { key: 'date_received', label: 'Received' },
  { key: 'idr_due', label: 'IDR due' },
  { key: 'status', label: 'Status' },
  { key: 'lodged_by', label: 'Lodged by' },
  { key: 'assigned_to', label: 'Assigned to' },
];

export function ComplaintsList() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lodgedByInput, setLodgedByInput] = useState('');
  const [lodgedBy, setLodgedBy] = useState('');
  const [assignedToInput, setAssignedToInput] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'Open' | 'Closed' | 'Sensitive' | 'Overdue'>('');
  const [idrDueFrom, setIdrDueFrom] = useState('');
  const [idrDueTo, setIdrDueTo] = useState('');
  const [sort, setSort] = useState<ComplaintSortColumn | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);

  const toggleSort = (column: ComplaintSortColumn) => {
    if (sort === column) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder('asc');
    }
    setPage(1);
  };

  const filters = {
    search,
    status: statusFilter === 'Open' || statusFilter === 'Closed' ? statusFilter : undefined,
    lodged_by: lodgedBy || undefined,
    assigned_to: assignedTo || undefined,
    overdue: statusFilter === 'Overdue' || undefined,
    sensitive: statusFilter === 'Sensitive' || undefined,
    idr_due_from: idrDueFrom || undefined,
    idr_due_to: idrDueTo || undefined,
    sort: sort ?? undefined,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['complaints', filters],
    queryFn: () => api.listComplaints(filters),
    placeholderData: keepPreviousData,
  });

  const applySearch = () => {
    setSearch(searchInput.trim());
    setLodgedBy(lodgedByInput.trim());
    setAssignedTo(assignedToInput.trim());
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="page">
      <h1 className="page-title">All complaints</h1>
      <p className="page-subtitle">
        Search by reference, application number, loan number or complainant name.
      </p>

      <div className="toolbar">
        <Field label="Search" htmlFor="search">
          <input
            id="search"
            value={searchInput}
            placeholder="CMP-000012, APP-2025-0001, Jane Smith…"
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
        </Field>
        <Field label="Status" htmlFor="status">
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
            <option value="Sensitive">Sensitive</option>
            <option value="Overdue">IDR overdue</option>
          </select>
        </Field>
        <Field label="Logged by" htmlFor="lodged_by">
          <input
            id="lodged_by"
            value={lodgedByInput}
            placeholder="name@homepro.com.au"
            onChange={(e) => setLodgedByInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
        </Field>
        <Field label="Assigned to" htmlFor="assigned_to">
          <input
            id="assigned_to"
            value={assignedToInput}
            placeholder="name@homepro.com.au"
            onChange={(e) => setAssignedToInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
        </Field>
        <div style={{ display: 'flex', gap: 10 }}>
          <Field label="IDR due from" htmlFor="idr_due_from">
            <input
              id="idr_due_from"
              type="date"
              value={idrDueFrom}
              max={idrDueTo || undefined}
              onChange={(e) => {
                setIdrDueFrom(e.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="IDR due to" htmlFor="idr_due_to">
            <input
              id="idr_due_to"
              type="date"
              value={idrDueTo}
              min={idrDueFrom || undefined}
              onChange={(e) => {
                setIdrDueTo(e.target.value);
                setPage(1);
              }}
            />
          </Field>
        </div>
        <button type="button" className="btn" onClick={applySearch}>
          <Search size={15} aria-hidden /> Search
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <Loading />
        ) : isError ? (
          <div className="notice error" style={{ margin: 16 }}>
            {(error as Error).message}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="empty">No complaints match those filters.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {sort === col.key ? (
                          order === 'asc' ? (
                            <ArrowUp size={12} aria-hidden />
                          ) : (
                            <ArrowDown size={12} aria-hidden />
                          )
                        ) : (
                          <ArrowUpDown size={12} aria-hidden style={{ opacity: 0.3 }} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* React escapes all interpolated values, so complaint text
                    cannot inject markup the way it could in the prototype. */}
                {data.items.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(`/complaints/${c.id}`)}>
                    <td className="reference">{c.reference}</td>
                    <td>
                      {c.first_name} {c.last_name}
                    </td>
                    <td className="muted">{c.application_number || c.loan_number || '—'}</td>
                    <td>{formatDateTime(c.date_received)}</td>
                    <td>{c.status === 'Open' ? formatDateTime(c.idr_response_due_at) : '—'}</td>
                    <td style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <StatusBadge status={c.status} />
                      <SensitiveBadge sensitive={c.is_sensitive} />
                      <OverdueBadge overdue={c.is_overdue} />
                    </td>
                    <td className="faint" style={{ fontSize: 12 }}>
                      {c.lodged_by}
                    </td>
                    <td className="faint" style={{ fontSize: 12 }}>
                      {c.assigned_to || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12 }}>
          <span className="muted">
            {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of{' '}
            {data.total}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <button
            type="button"
            className="btn small"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
